import { useEffect, useState, type ReactElement } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Navigate } from "@tanstack/react-router";
import { LoaderCircle } from "lucide-react";

import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";

import {
  BillingApiError,
  eventLabel,
  fetchBalance,
  fetchHistory,
  fetchPlans,
  fetchUsage,
  formatCredits,
  formatDate,
  formatUsd,
  grantLabel,
  openBillingPortal,
  saveAutoTopup,
  saveCheckInLevel,
  saveCloudJobBudget,
  startTierCheckout,
  startTopupCheckout,
  type CreditBalance,
} from "./api";

/** "Plans & credit usage" — the owner's whole billing surface. */
export function Billing(): ReactElement {
  useEffect(() => {
    const previous = document.title;
    document.title = "Billing — Chill";
    return () => {
      document.title = previous;
    };
  }, []);
  const balanceQuery = useQuery({
    queryKey: ["credit-balance"],
    queryFn: fetchBalance,
    retry: false,
  });

  if (balanceQuery.isPending) return <BillingLoading />;
  if (
    balanceQuery.error instanceof BillingApiError &&
    balanceQuery.error.status === 401
  ) {
    return <Navigate to="/login" replace />;
  }
  if (balanceQuery.isError || !balanceQuery.data) {
    return (
      <Shell>
        <Card>
          <CardHeader>
            <CardTitle>Couldn't load your credits</CardTitle>
            <CardDescription>
              {balanceQuery.error?.message ?? "Please try again."}
            </CardDescription>
          </CardHeader>
          <CardContent>
            <Button onClick={() => void balanceQuery.refetch()}>Try again</Button>
          </CardContent>
        </Card>
      </Shell>
    );
  }

  return (
    <Shell>
      <BalanceSummary balance={balanceQuery.data} />

      <Tabs defaultValue="breakdown" className="mt-8">
        <TabsList>
          <TabsTrigger value="breakdown">Breakdown</TabsTrigger>
          <TabsTrigger value="usage">Usage details</TabsTrigger>
          <TabsTrigger value="history">History</TabsTrigger>
          <TabsTrigger value="plans">Plans</TabsTrigger>
          <TabsTrigger value="settings">Settings</TabsTrigger>
        </TabsList>

        <TabsContent value="breakdown" className="mt-6">
          <BreakdownTab balance={balanceQuery.data} />
        </TabsContent>
        <TabsContent value="usage" className="mt-6">
          <UsageTab />
        </TabsContent>
        <TabsContent value="history" className="mt-6">
          <HistoryTab />
        </TabsContent>
        <TabsContent value="plans" className="mt-6">
          <PlansTab balance={balanceQuery.data} />
        </TabsContent>
        <TabsContent value="settings" className="mt-6">
          <SettingsTab balance={balanceQuery.data} />
        </TabsContent>
      </Tabs>
    </Shell>
  );
}

// ── Summary ─────────────────────────────────────────────────────────────────

function BalanceSummary({ balance }: { balance: CreditBalance }): ReactElement {
  const portal = useMutation({
    mutationFn: openBillingPortal,
    onSuccess: ({ url }) => {
      window.location.href = url;
    },
  });

  return (
    <Card>
      <CardHeader className="flex flex-row flex-wrap items-start justify-between gap-4">
        <div>
          <CardDescription>Credits remaining</CardDescription>
          <CardTitle className="mt-1 text-4xl tabular-nums">
            {formatCredits(balance.totalCredits)}
          </CardTitle>
          <p className="mt-2 text-sm text-[#74766f]">
            {balance.planLabel} plan
            {balance.monthlyCredits > 0
              ? ` · ${formatCredits(balance.monthlyCredits)} credits a month`
              : ""}
            {balance.currentPeriodEnd
              ? ` · renews ${formatDate(balance.currentPeriodEnd)}`
              : ""}
          </p>
        </div>
        {balance.planId !== "free" ? (
          <Button
            variant="outline"
            disabled={portal.isPending}
            onClick={() => portal.mutate()}
          >
            {portal.isPending ? "Opening…" : "Manage billing"}
          </Button>
        ) : null}
      </CardHeader>
      <CardContent className="grid gap-4 sm:grid-cols-3">
        <Stat label="General credits" value={balance.generalCredits} />
        <Stat
          label="Daily build credits"
          value={balance.usageSpecificCredits.build}
          hint="Resets at midnight UTC"
        />
        <Stat
          label="Hosting + AI credits"
          value={
            balance.usageSpecificCredits.cloud + balance.usageSpecificCredits.ai
          }
          hint="Included with your plan each month"
        />
      </CardContent>
    </Card>
  );
}

function Stat({
  label,
  value,
  hint,
}: {
  label: string;
  value: number;
  hint?: string;
}): ReactElement {
  return (
    <div className="rounded-lg border border-[#e2e2dc] p-4">
      <p className="text-xs uppercase tracking-wide text-[#74766f]">{label}</p>
      <p className="mt-1 text-2xl tabular-nums text-[#171816]">
        {formatCredits(value)}
      </p>
      {hint ? <p className="mt-1 text-xs text-[#8b8d85]">{hint}</p> : null}
    </div>
  );
}

// ── Breakdown ───────────────────────────────────────────────────────────────

function BreakdownTab({ balance }: { balance: CreditBalance }): ReactElement {
  if (balance.grants.length === 0) {
    return <Empty>You don't have any credits right now.</Empty>;
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-base">Credit grants</CardTitle>
        <CardDescription>
          Credits closest to expiring are always used first, so you lose as
          little as possible.
        </CardDescription>
      </CardHeader>
      <CardContent className="divide-y divide-[#e2e2dc]">
        {balance.grants.map((grant) => (
          <div
            key={grant.grantId}
            className="flex items-center justify-between gap-4 py-3 first:pt-0 last:pb-0"
          >
            <div>
              <p className="text-sm font-medium text-[#171816]">
                {grantLabel(grant.type)}
              </p>
              <p className="text-xs text-[#74766f]">
                Expires {formatDate(grant.expiresAt)}
                {grant.scope !== "general" ? ` · ${grant.scope} only` : ""}
              </p>
            </div>
            <p className="text-sm tabular-nums text-[#171816]">
              {formatCredits(grant.remainingCredits)}
              <span className="text-[#8b8d85]">
                {" "}
                / {formatCredits(grant.amountCredits)}
              </span>
            </p>
          </div>
        ))}
      </CardContent>
    </Card>
  );
}

// ── Usage ───────────────────────────────────────────────────────────────────

function UsageTab(): ReactElement {
  const [days, setDays] = useState(30);
  const since = new Date(Date.now() - days * 86_400_000).toISOString();
  const usageQuery = useQuery({
    queryKey: ["credit-usage", days],
    queryFn: () => fetchUsage(since),
  });

  if (usageQuery.isPending) return <BillingLoading />;
  if (!usageQuery.data) return <Empty>Couldn't load usage.</Empty>;

  const usage = usageQuery.data;

  return (
    <div className="space-y-6">
      <div className="flex gap-2">
        {[7, 30, 90].map((n) => (
          <Button
            key={n}
            size="sm"
            variant={days === n ? "default" : "outline"}
            onClick={() => setDays(n)}
          >
            Last {n} days
          </Button>
        ))}
      </div>

      <Card>
        <CardHeader>
          <CardDescription>Credits used</CardDescription>
          <CardTitle className="text-3xl tabular-nums">
            {formatCredits(usage.totalCredits)}
          </CardTitle>
        </CardHeader>
        <CardContent className="grid gap-4 sm:grid-cols-3">
          <Stat label="Building" value={usage.byKind.build} />
          <Stat label="Hosting" value={usage.byKind.cloud} />
          <Stat label="AI features" value={usage.byKind.ai} />
        </CardContent>
      </Card>

      {usage.byProject.length > 0 ? (
        <Card>
          <CardHeader>
            <CardTitle className="text-base">By site</CardTitle>
          </CardHeader>
          <CardContent className="divide-y divide-[#e2e2dc]">
            {usage.byProject.map((row) => (
              <div
                key={row.projectId}
                className="flex justify-between py-2 text-sm first:pt-0 last:pb-0"
              >
                <span className="text-[#171816]">{row.projectId}</span>
                <span className="tabular-nums text-[#74766f]">
                  {formatCredits(row.credits)}
                </span>
              </div>
            ))}
          </CardContent>
        </Card>
      ) : null}

      {usage.records.length === 0 ? (
        <Empty>No credits used in this period.</Empty>
      ) : null}
    </div>
  );
}

// ── History ─────────────────────────────────────────────────────────────────

function HistoryTab(): ReactElement {
  const historyQuery = useQuery({
    queryKey: ["credit-history"],
    queryFn: () => fetchHistory(100),
  });

  if (historyQuery.isPending) return <BillingLoading />;
  const entries = historyQuery.data?.entries ?? [];
  if (entries.length === 0) return <Empty>No credit activity yet.</Empty>;

  return (
    <Card>
      <CardContent className="divide-y divide-[#e2e2dc] pt-6">
        {entries.map((entry) => (
          <div
            key={entry.id}
            className="flex items-center justify-between gap-4 py-3 first:pt-0 last:pb-0"
          >
            <div>
              <p className="text-sm text-[#171816]">{eventLabel(entry.event)}</p>
              <p className="text-xs text-[#74766f]">
                {formatDate(entry.occurredAt)}
                {entry.projectId ? ` · ${entry.projectId}` : ""}
                {entry.description ? ` · ${entry.description}` : ""}
              </p>
            </div>
            <span
              className={
                entry.credits >= 0
                  ? "text-sm tabular-nums text-[#2f6b4f]"
                  : "text-sm tabular-nums text-[#74766f]"
              }
            >
              {entry.credits >= 0 ? "+" : ""}
              {formatCredits(entry.credits)}
            </span>
          </div>
        ))}
      </CardContent>
    </Card>
  );
}

// ── Plans ───────────────────────────────────────────────────────────────────

function PlansTab({ balance }: { balance: CreditBalance }): ReactElement {
  const plansQuery = useQuery({ queryKey: ["credit-plans"], queryFn: fetchPlans });
  const [cycle, setCycle] = useState<"monthly" | "annual">("monthly");

  const checkout = useMutation({
    mutationFn: startTierCheckout,
    onSuccess: ({ url }) => {
      if (url) window.location.href = url;
    },
  });

  if (plansQuery.isPending) return <BillingLoading />;
  const paidPlans = (plansQuery.data?.plans ?? []).filter((p) => p.id !== "free");

  return (
    <div className="space-y-6">
      <div className="flex gap-2">
        {(["monthly", "annual"] as const).map((c) => (
          <Button
            key={c}
            size="sm"
            variant={cycle === c ? "default" : "outline"}
            onClick={() => setCycle(c)}
          >
            {c === "monthly" ? "Monthly" : "Annual (save ~17%)"}
          </Button>
        ))}
      </div>

      {paidPlans.map((plan) => (
        <Card key={plan.id}>
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-base">
              {plan.label}
              {balance.planId === plan.id ? <Badge>Current</Badge> : null}
            </CardTitle>
            <CardDescription>
              {plan.dailyBuildCredits} free build credits a day, plus{" "}
              {plan.cloudGrantCredits} hosting and {plan.aiGrantCredits} AI
              credits a month. Unused credits roll over.
            </CardDescription>
          </CardHeader>
          <CardContent className="grid gap-2 sm:grid-cols-2 lg:grid-cols-3">
            {plan.tiers.map((tier) => {
              const current =
                balance.planId === plan.id &&
                balance.monthlyCredits === tier.monthlyCredits &&
                balance.billingCycle === cycle;
              const cents =
                cycle === "annual" ? tier.annualPriceCents : tier.monthlyPriceCents;
              return (
                <button
                  key={tier.monthlyCredits}
                  type="button"
                  disabled={current || checkout.isPending}
                  onClick={() =>
                    checkout.mutate({
                      planId: plan.id as "pro" | "business",
                      monthlyCredits: tier.monthlyCredits,
                      billingCycle: cycle,
                    })
                  }
                  className="flex items-center justify-between rounded-lg border border-[#e2e2dc] px-3 py-2 text-left text-sm transition hover:border-[#20272b] disabled:cursor-default disabled:opacity-50"
                >
                  <span className="tabular-nums text-[#171816]">
                    {tier.monthlyCredits.toLocaleString()} credits
                  </span>
                  <span className="tabular-nums text-[#74766f]">
                    {formatUsd(cents)}
                    {cycle === "annual" ? "/yr" : "/mo"}
                  </span>
                </button>
              );
            })}
          </CardContent>
        </Card>
      ))}

      {checkout.isError ? (
        <p className="text-sm text-[#b4482f]">
          {(checkout.error as Error).message}
        </p>
      ) : null}
    </div>
  );
}

// ── Settings ────────────────────────────────────────────────────────────────

function SettingsTab({ balance }: { balance: CreditBalance }): ReactElement {
  const queryClient = useQueryClient();
  const [topupCredits, setTopupCredits] = useState(100);
  const [autoEnabled, setAutoEnabled] = useState(
    balance.autoTopup?.enabled ?? false,
  );
  const [threshold, setThreshold] = useState(
    balance.autoTopup?.thresholdCredits ?? 20,
  );
  const [amount, setAmount] = useState(balance.autoTopup?.amountCredits ?? 100);
  const [checkIn, setCheckIn] = useState(balance.checkInLevelCredits);
  const [cloudJobBudget, setCloudJobBudget] = useState(
    balance.cloudJobBudgetCredits,
  );

  const topup = useMutation({
    mutationFn: () => startTopupCheckout(topupCredits),
    onSuccess: ({ url }) => {
      if (url) window.location.href = url;
    },
  });

  const auto = useMutation({
    mutationFn: () =>
      saveAutoTopup({
        enabled: autoEnabled,
        thresholdCredits: threshold,
        amountCredits: amount,
      }),
    onSuccess: () =>
      queryClient.invalidateQueries({ queryKey: ["credit-balance"] }),
  });

  const checkInMutation = useMutation({
    mutationFn: () => saveCheckInLevel(checkIn),
    onSuccess: () =>
      queryClient.invalidateQueries({ queryKey: ["credit-balance"] }),
  });

  const cloudJobBudgetMutation = useMutation({
    mutationFn: () => saveCloudJobBudget(cloudJobBudget),
    onSuccess: () =>
      queryClient.invalidateQueries({ queryKey: ["credit-balance"] }),
  });

  const paid = balance.planId !== "free";

  return (
    <div className="space-y-6">
      <Card>
        <CardHeader>
          <CardTitle className="text-base">Cloud job budget</CardTitle>
          <CardDescription>
            Reserve up to this many credits for each cloud coding attempt. If
            the work needs more, it pauses with your progress saved.
          </CardDescription>
        </CardHeader>
        <CardContent className="flex flex-wrap items-end gap-3">
          <div className="w-40">
            <Label htmlFor="cloud-job-budget">Credits</Label>
            <Input
              id="cloud-job-budget"
              type="number"
              min={1.38}
              max={100_000}
              step={0.01}
              value={cloudJobBudget}
              onChange={(e) => setCloudJobBudget(Number(e.target.value))}
            />
          </div>
          <Button
            variant="outline"
            disabled={
              cloudJobBudgetMutation.isPending || cloudJobBudget < 1.38
            }
            onClick={() => cloudJobBudgetMutation.mutate()}
          >
            {cloudJobBudgetMutation.isPending ? "Saving…" : "Save"}
          </Button>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle className="text-base">Buy credits</CardTitle>
          <CardDescription>
            One-time top-ups never expire for a year and are used after your
            monthly credits.
          </CardDescription>
        </CardHeader>
        <CardContent className="flex flex-wrap items-end gap-3">
          <div className="w-40">
            <Label htmlFor="topup-credits">Credits</Label>
            <Input
              id="topup-credits"
              type="number"
              min={10}
              step={10}
              value={topupCredits}
              onChange={(e) => setTopupCredits(Number(e.target.value))}
            />
          </div>
          <Button
            disabled={!paid || topup.isPending || topupCredits < 10}
            onClick={() => topup.mutate()}
          >
            {topup.isPending ? "Opening…" : `Buy for ${formatUsd(topupCredits * 25)}`}
          </Button>
          {!paid ? (
            <p className="text-sm text-[#74766f]">
              Top-ups are available on paid plans.
            </p>
          ) : null}
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle className="text-base">Auto top-up</CardTitle>
          <CardDescription>
            Automatically buy more credits when you're running low, so the agent
            never stops mid-project.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <label className="flex items-center gap-2 text-sm">
            <input
              type="checkbox"
              checked={autoEnabled}
              disabled={!paid}
              onChange={(e) => setAutoEnabled(e.target.checked)}
            />
            Enable auto top-up
          </label>
          <div className="flex flex-wrap gap-3">
            <div className="w-40">
              <Label htmlFor="auto-threshold">When below</Label>
              <Input
                id="auto-threshold"
                type="number"
                min={0}
                value={threshold}
                disabled={!paid || !autoEnabled}
                onChange={(e) => setThreshold(Number(e.target.value))}
              />
            </div>
            <div className="w-40">
              <Label htmlFor="auto-amount">Buy</Label>
              <Input
                id="auto-amount"
                type="number"
                min={10}
                step={10}
                value={amount}
                disabled={!paid || !autoEnabled}
                onChange={(e) => setAmount(Number(e.target.value))}
              />
            </div>
          </div>
          <Button
            variant="outline"
            disabled={!paid || auto.isPending}
            onClick={() => auto.mutate()}
          >
            {auto.isPending ? "Saving…" : "Save"}
          </Button>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle className="text-base">Long-running message limit</CardTitle>
          <CardDescription>
            The most credits a single message can use before the agent stops and
            tells you what it finished. Set to 0 to turn this off.
          </CardDescription>
        </CardHeader>
        <CardContent className="flex flex-wrap items-end gap-3">
          <div className="w-40">
            <Label htmlFor="check-in">Credits</Label>
            <Input
              id="check-in"
              type="number"
              min={0}
              value={checkIn}
              onChange={(e) => setCheckIn(Number(e.target.value))}
            />
          </div>
          <Button
            variant="outline"
            disabled={checkInMutation.isPending}
            onClick={() => checkInMutation.mutate()}
          >
            {checkInMutation.isPending ? "Saving…" : "Save"}
          </Button>
        </CardContent>
      </Card>
    </div>
  );
}

// ── Chrome ──────────────────────────────────────────────────────────────────

function Shell({ children }: { children: React.ReactNode }): ReactElement {
  return (
    <div className="signed-in-billing px-4 py-8 text-[#20272b] sm:px-8 sm:py-12 [&_.bg-card]:bg-white/88 [&_.shadow]:shadow-[0_16px_44px_rgba(91,139,174,0.08)]">
      <div className="mx-auto max-w-5xl">
        <h1 className="font-['Avenir_Next',Inter,sans-serif] text-3xl font-semibold tracking-[-0.045em] sm:text-[2.5rem]">Billing</h1>
        <p className="mt-2 text-sm text-[#52606e]">
          Plans, credits, and payment settings for your account.
        </p>
        <div className="mt-8">{children}</div>
      </div>
    </div>
  );
}

function BillingLoading(): ReactElement {
  return (
    <div className="flex min-h-40 items-center justify-center text-[#62716c]">
      <LoaderCircle className="size-5 animate-spin" aria-label="Loading" />
    </div>
  );
}

function Empty({ children }: { children: React.ReactNode }): ReactElement {
  return (
    <div className="rounded-lg border border-dashed border-[#d7d7d0] px-6 py-10 text-center text-sm text-[#74766f]">
      {children}
    </div>
  );
}
